import { ArrowLeft, Activity, TrendingUp } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { PatientLayout } from './patient/PatientLayout';

interface HomeTestSelectionProps {
  onBack?: () => void;
  onSelectTest: (test: 'spirometry' | 'sixmwt') => void;
  selectedPatient?: any;
  onNavigate?: (view: string) => void;
  onLogout?: () => void;
}

export function HomeTestSelection({ onBack, onSelectTest, selectedPatient, onNavigate, onLogout }: HomeTestSelectionProps) {
  // ✅ If selectedPatient exists, this is being used by therapist/nurse - don't wrap in PatientLayout
  // ✅ If no selectedPatient, this is being used by patient - wrap in PatientLayout
  const content = (
    <>
      {/* Header */}
      <div className="bg-primary p-6 rounded-b-3xl shadow-md">
        <div className="flex items-center gap-4">
          {onBack && (
            <button onClick={onBack} className="text-primary-foreground">
              <ArrowLeft className="w-6 h-6" />
            </button>
          )}
          <div className="flex-1">
            <h1 className="text-primary-foreground">Start Home Test</h1>
            {selectedPatient ? (
              <p className="text-primary-foreground/80">
                Patient: {selectedPatient.name} ({selectedPatient.id})
              </p>
            ) : (
              <p className="text-primary-foreground/80">Choose a test to perform</p>
            )}
          </div>
        </div>
      </div>

      <div className="p-4 space-y-4">
        {/* Spirometry Test Card */}
        <Card className="shadow-lg hover:shadow-xl transition-shadow cursor-pointer">
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                <Activity className="w-6 h-6 text-primary" />
              </div>
              <div className="flex-1">
                <CardTitle className="text-primary">Spirometry Test</CardTitle>
                <p className="text-muted-foreground">Lung function measurement</p>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <h3 className="text-primary">What it measures:</h3>
              <ul className="space-y-1 text-muted-foreground list-disc list-inside">
                <li>FEV1 (Forced Expiratory Volume)</li>
                <li>FVC (Forced Vital Capacity)</li>
                <li>Peak Flow</li>
                <li>FEV1/FVC Ratio</li>
              </ul>
            </div>

            <div className="space-y-2">
              <h3 className="text-primary">Test duration:</h3>
              <p className="text-muted-foreground">Approximately 15-20 minutes</p>
            </div>

            <div className="space-y-2">
              <h3 className="text-primary">Requirements:</h3>
              <ul className="space-y-1 text-muted-foreground list-disc list-inside">
                <li>Spirometer device (Bluetooth connected)</li>
                <li>No smoking 1 hour before</li>
                <li>No heavy meal 2 hours before</li>
                <li>Follow ATS/ERS 2019 standards</li>
              </ul>
            </div>

            <Button 
              className="w-full bg-primary hover:bg-primary/90 h-12 rounded-xl"
              onClick={() => onSelectTest('spirometry')}
            >
              Start Spirometry Test
            </Button>
          </CardContent>
        </Card>

        {/* 6-Minute Walk Test Card */}
        <Card className="shadow-lg hover:shadow-xl transition-shadow cursor-pointer">
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-accent/10 flex items-center justify-center">
                <TrendingUp className="w-6 h-6 text-accent" />
              </div>
              <div className="flex-1">
                <CardTitle className="text-accent">6-Minute Walk Test</CardTitle>
                <p className="text-muted-foreground">Functional capacity assessment</p>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <h3 className="text-primary">What it measures:</h3>
              <ul className="space-y-1 text-muted-foreground list-disc list-inside">
                <li>Walking distance in 6 minutes</li>
                <li>Heart rate & blood pressure response</li>
                <li>Oxygen saturation (SpO₂)</li>
                <li>Breathlessness (Borg scale)</li>
                <li>SPPB Score & Grip Strength</li>
              </ul>
            </div>

            <div className="space-y-2">
              <h3 className="text-primary">Test duration:</h3>
              <p className="text-muted-foreground">6 minutes walking + vitals recording (total ~15 minutes)</p>
            </div>

            <div className="space-y-2">
              <h3 className="text-primary">Requirements:</h3>
              <ul className="space-y-1 text-muted-foreground list-disc list-inside">
                <li>Clear, flat walking path (corridor/hallway)</li>
                <li>Pulse oximeter</li>
                <li>Blood pressure monitor</li>
                <li>Comfortable walking shoes</li>
              </ul>
            </div>

            <Button 
              className="w-full bg-accent hover:bg-accent/90 h-12 rounded-xl"
              onClick={() => onSelectTest('sixmwt')}
            >
              Start 6-Minute Walk Test
            </Button>
          </CardContent>
        </Card>

        {/* Info Card */}
        <Card className="bg-primary/5 border-primary/20">
          <CardContent className="p-4">
            <h3 className="text-primary mb-2">Important Notes</h3>
            <ul className="space-y-1 text-muted-foreground list-disc list-inside">
              <li>Ensure you have all required equipment before starting</li>
              <li>Follow all test preparation guidelines</li>
              <li>Stop immediately if you feel chest pain, severe breathlessness, or dizziness</li>
              <li>Results will be automatically shared with your healthcare team</li>
            </ul>
          </CardContent>
        </Card>
      </div>
    </>
  );

  // ✅ If viewing as therapist/nurse (selectedPatient exists), render without PatientLayout
  if (selectedPatient) {
    return <div className="min-h-screen bg-[#D4F0F8]">{content}</div>;
  }

  // ✅ If viewing as patient (no selectedPatient), wrap in PatientLayout
  return (
    <PatientLayout 
      currentView="hometest"
      onNavigate={onNavigate}
      onLogout={onLogout}
    >
      {content}
    </PatientLayout>
  );
}